/**
 * Colourway Composer V2 — UI MOCK (no backend wiring).
 *
 * Standalone preview of the BOM-attachment flow described in the Feb 2026
 * Bidso SKU re-creation spec. Mounted at /colourway-mock so the user can
 * sign off on UX before the real backend ships. All data is local dummy.
 *
 * Flow steps:
 *   1. Pick target (DRAFT_NO_BOM) + source (any ACTIVE bidso_sku)
 *   2. Editable parts (INP/INM) shown blank → designer picks pantone per row.
 *      Non-editable parts (ACC etc.) shown as carried-over chips.
 *      "+ Add / Replace part" opens mould master picker (active only).
 *   3. Review — duplicate-BOM check, new INP vs existing INP split,
 *      then submit → PENDING_REVIEW.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Palette, ArrowRight, ArrowLeft, Plus, Replace, Check, AlertTriangle,
  CircleDot, Lock, Search, Send, Sparkles, RotateCcw, Edit2, MessageSquareWarning, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Dummy data (mock only) ----------
// Pre-existing Bidso SKUs in DRAFT_NO_BOM status with **no compose work started yet**.
// Tile 1 ("Attach BOM") picks from this list — the codes already exist; the user just imports/clones a BOM onto them.
const EMPTY_BOM_SKUS = [
  { bidso_sku_id: "KS_BE_PE_143", name: "Plaque Bicycle 143", created: "2026-02-05" },
  { bidso_sku_id: "KS_BE_PE_144", name: "Plaque Bicycle 144", created: "2026-02-05" },
  { bidso_sku_id: "KS_WV_021",     name: "Wave Bike 021",     created: "2026-02-04" },
  { bidso_sku_id: "KT_PX_PE_058",  name: "Pixi Plaque Trike 058", created: "2026-02-04" },
];
// Drafts that already have **partial compose work saved** — Tile 2 list now
// comes from `GET /api/bidso-composer/incomplete` (the `liveIncompleteDrafts`
// state in the component). No hard-coded mock entries remain.
// Source picker (Step 1 + Tile 3) — now pulled live from
// `GET /api/bidso-composer/active-skus?q=` via the `liveActiveSkus` state.
// The dummy SOURCE_SKUS list has been retired.
// Source BOM is now fetched live via `fetchSourceBomRows` →
// GET /api/demand-hub/bidso-skus/{id}/bom-for-clone. The dummy SOURCE_BOM
// constant has been retired.
// ALL_MOULDS list is now fetched live via `liveMoulds` → GET /api/moulds?status=ACTIVE.
const PANTONE_LIBRARY = [
  { code: "PMS-9999X",     name: "PMS 9999 X",     hex: "#1A3D7A" },
  { code: "PMS-185C",      name: "PMS 185 C",      hex: "#E4002B" },
  { code: "PMS-Yellow-C",  name: "PMS Yellow C",   hex: "#FEDD00" },
  { code: "PMS-355C",      name: "PMS 355 C",      hex: "#009639" },
  { code: "PMS-Black-C",   name: "PMS Black C",    hex: "#101820" },
  { code: "PMS-White-C",   name: "PMS White C",    hex: "#F4F5F0" },
];
// Existing INPs keyed by (mould_code|pantone_code) — duplicate-detection truth source for the mock
const EXISTING_INPS = {
  "RO4-12|PMS-9999X": "INP_655",
  "RO4-15|PMS-185C":  "INP_660",
  "RO4-17|PMS-355C":  "INP_701",
  "RM-22|PMS-Yellow-C": "INM_412",
  "RM-23|PMS-Yellow-C": "INM_413",
};

// ---------- Helpers ----------
const stepLabel = (n) => ({1: "Select", 2: "Compose", 3: "Review"}[n]);
const isEditable = (cat) => cat === "INP" || cat === "INM" || cat === "ACC";

// All landing-page counts (empty-bom / incomplete / active / sent-back) now
// come from `GET /api/bidso-composer/landing-stats` (the `liveStats` state).
// No hard-coded COUNTS or SENT_BACK_ITEMS constants remain.

// ---------- Component ----------
export default function ColourwayMock() {
  // view: 'landing' | 'wizard' (3-step BOM attach) | 'path3-stub' (Path 3 link) | 'resume-picker' (>1 drafts)
  const [view, setView] = useState("landing");
  const [step, setStep] = useState(1);
  const [entryReason, setEntryReason] = useState(null); // "attach" | "resume" | "sent-back"
  // Tile 1 (Attach BOM) — pick an existing DRAFT_NO_BOM SKU + a source ACTIVE SKU to clone from.
  const [targetId, setTargetId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [rows, setRows] = useState([]);
  const [mouldPickerOpen, setMouldPickerOpen] = useState(false);
  // ACC picker (Feb 2026 — "Add accessory" alongside "Add part"). Empty by
  // default; only searches once the user types ≥2 chars so the catalog
  // doesn't dump on open.
  const [accPickerOpen, setAccPickerOpen] = useState(false);
  const [accSearch, setAccSearch] = useState("");
  const [accResults, setAccResults] = useState([]);
  const [accSearching, setAccSearching] = useState(false);
  const [accDupNoticeFor, setAccDupNoticeFor] = useState("");
  const [replaceRowIdx, setReplaceRowIdx] = useState(null);
  const [mouldSearch, setMouldSearch] = useState("");
  const [recolourSearch, setRecolourSearch] = useState("");
  const [loadingBom, setLoadingBom] = useState(false);
  const [liveMoulds, setLiveMoulds] = useState([]);
  const [livePantones, setLivePantones] = useState([]);
  // Live dedup preview keyed by row index. Populated by a debounced call
  // to `POST /api/bidso-composer/preview-row-dedup` whenever editable rows
  // change. Same `find_duplicate_rm` the commit path uses → the green
  // "→ INP_xxx" badge can never disagree with what commit will do.
  // Shape: { [rowIdx]: { rm_id?, action: "existing" | "new", category } }
  const [dedupPreviews, setDedupPreviews] = useState({});
  // True while the debounce timer is pending OR the POST is in flight.
  // Drives the "checking…" badge so a row never falsely shows "new INP"
  // when the backend hasn't been consulted yet. Also gates Step 3 review
  // derivation so commit can't ship while previews are stale.
  const [dedupChecking, setDedupChecking] = useState(false);
  const dedupReqIdRef = useRef(0);
  // Path 1 Step 2 toggle: "blank" (default) clears INP/INM pantones for fresh
  // colour picks; "preserve" carries them over from the source SKU. Path 3
  // doesn't read this state — it always starts in preserve mode.
  const [attachColourMode, setAttachColourMode] = useState("blank");

  // Flip every editable row's pantone in-place when the toggle is changed.
  // Non-editable (ACC/hardware) rows are untouched regardless.
  const applyAttachColourMode = (mode) => {
    setAttachColourMode(mode);
    setRows((prev) => prev.map((r) => {
      if (!r._editable) return r;
      return { ...r, pantone: mode === "blank" ? "" : (r._source_pantone || "") };
    }));
  };

  // ----- Live backend wiring (real data, not mock) -----
  const token = useAuthStore((s) => s.token);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [liveEmptyBomSkus, setLiveEmptyBomSkus] = useState([]);     // GET /empty-bom-skus
  const [liveActiveSkus, setLiveActiveSkus] = useState([]);          // GET /active-skus?q=
  const [liveIncompleteDrafts, setLiveIncompleteDrafts] = useState([]); // GET /incomplete
  const [liveStats, setLiveStats] = useState({                        // GET /landing-stats
    empty_bom_count: 0, incomplete_count: 0, active_count: 0,
    pending_review_count: 0, sent_back_count: 0, sent_back_items: [],
  });
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Re-fetch the DRAFT_NO_BOM list whenever the parent broadcasts a
  // `bidso-composer:refresh` event (e.g. after a draft is created from the
  // SKU Management dialog). Keeps the Tile 1 picker in sync without a route
  // change.
  useEffect(() => {
    const handler = () => setRefreshNonce((n) => n + 1);
    window.addEventListener("bidso-composer:refresh", handler);
    return () => window.removeEventListener("bidso-composer:refresh", handler);
  }, []);

  // Fetch live landing data — stats + Tile 1 picker + Tile 2 list.
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const [stats, empty, incomplete] = await Promise.all([
          axios.get(`${API}/bidso-composer/landing-stats`, { headers: authHeaders }),
          axios.get(`${API}/bidso-composer/empty-bom-skus`, { headers: authHeaders }),
          axios.get(`${API}/bidso-composer/incomplete`, { headers: authHeaders }),
        ]);
        if (cancel) return;
        setLiveStats(stats.data || {});
        setLiveEmptyBomSkus(empty.data?.items || []);
        setLiveIncompleteDrafts(incomplete.data?.items || []);
      } catch (e) {
        console.warn("Composer landing fetch failed", e);
      }
    })();
    return () => { cancel = true; };
  }, [token, authHeaders, refreshNonce]);

  // Search ACTIVE SKUs as user types (Tile 3 + Step 1 source picker) — light debounce.
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourcePopoverOpen, setSourcePopoverOpen] = useState(false);
  // Pull when either the recolour-picker view is shown OR the user is on Step 1 of the attach wizard.
  const _shouldFetchActive = view === "recolour-picker" || (view === "wizard" && step === 1);
  const _activeQuery = view === "recolour-picker" ? recolourSearch : sourceSearch;
  useEffect(() => {
    if (!token || !_shouldFetchActive) return;
    let cancel = false;
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/bidso-composer/active-skus`, {
          headers: authHeaders,
          params: { q: _activeQuery || "", limit: 50 },
        });
        if (!cancel) setLiveActiveSkus(res.data?.items || []);
      } catch (e) {
        if (!cancel) setLiveActiveSkus([]);
      }
    }, 250);
    return () => { cancel = true; clearTimeout(handle); };
  }, [token, authHeaders, _shouldFetchActive, _activeQuery]);

  const targetSku = EMPTY_BOM_SKUS.find(s => s.bidso_sku_id === targetId);
  const target = { bidso_sku_id: targetId, name: targetSku?.name || "(draft)" };

  // Load a draft's saved state and jump straight to Step 2 (Compose).
  // Used by Resume and Sent-back flows — no need to re-pick target/source.
  const resumeDraft = (draftId) => {
    const draft = liveIncompleteDrafts.find(d => d.bidso_sku_id === draftId);
    if (!draft) return;
    setTargetId(draft.bidso_sku_id);
    setSourceId(draft.source_bidso_sku_id || "");
    // wip_parts (if present) is the source of truth for resume — until that
    // backend buffer ships, we land the user on Step 1 to re-pick source.
    setRows([]);
    setView("wizard");
    setStep(draft.source_bidso_sku_id ? 2 : 1);
  };

  // ----- Sent-back: load Tech Ops's (possibly-edited) BOM into Step 2 -----
  // The designer sees exactly what Tech Ops left, can edit, and resubmit.
  // Sent-back metadata (status, review_remarks, tech_ops_edit_summary) is
  // surfaced as a sticky banner above Step 2 so the rationale is visible.
  const [sentBackInfo, setSentBackInfo] = useState(null);  // { bidso_sku_id, review_remarks, tech_ops_edit_summary, tech_ops_modified_at, tech_ops_modified_by_name }

  const loadSentBackForEdit = async (bidsoSkuId) => {
    try {
      const [detailRes, queueRes] = await Promise.all([
        axios.get(`${API}/bidso-composer/${encodeURIComponent(bidsoSkuId)}/bom-detail`, { headers: authHeaders }),
        axios.get(`${API}/bidso-composer/tech-ops/requests`, { headers: authHeaders }),
      ]);
      const items = detailRes.data?.items || [];
      const meta = (queueRes.data?.items || []).find(it => it.bidso_sku_id === bidsoSkuId) || {};
      // Map bom-detail items → wizard row format.
      const wizardRows = items.map(it => ({
        rm_id: it.rm_id,
        category: it.category,
        mould_code: it.mould_code || "—",
        // R-Composer-desc (Jul 2026, fix v2 Jul 2026).
        // DO NOT fall back to `it.rm_name` here — the backend defaults
        // `rm_name` to `rm_id` when `raw_materials.name` is missing, which
        // silently short-circuits the `part_name → description → rm_id`
        // render chain (see the same-line mapper below and the Step 2/3
        // JSX). For ACC rows the correct label is `description`; letting
        // `part_name` fall through to `""` lets the JSX chain resolve it.
        part_name: it.part_name || (it.category_data && it.category_data.part_name) || "",
        // R-Composer-desc (Jul 2026) — full readable RM description so
        // ACC rows (which have no `part_name`) render with a name.
        description: it.description || "",
        pantone: it.pantone_code || "",
        _source_pantone: it.pantone_code || "",
        _source_rm_id: it.rm_id,                  // marker: row already maps to an existing RM, no recreation
        _source_colourant_ids: [],
        // Jul 2026 (Colourway INMFAB legacy-row fix) — same stash as
        // in `fetchSourceBomRows`. `bidso_composer_routes.get_bom_detail`
        // now emits `parent_rm_id` + `source_type` for INM rows so the
        // sent-back edit flow round-trips the INMFAB link. When the
        // source INM row genuinely lacks a parent both stay falsy and
        // the "legacy row" guard toast at Step-3 commit still fires.
        _source_parent_rm_id: it.parent_rm_id || "",
        _source_source_type: it.source_type || "",
        qty: it.quantity || 1,
        unit: it.unit || "nos",
        _editable: it.category === "INP" || it.category === "INM",
        _origin: "sent-back",
      }));
      setTargetId(bidsoSkuId);
      setSourceId("");
      setRows(wizardRows);
      setSentBackInfo({
        bidso_sku_id: bidsoSkuId,
        review_remarks: meta.review_remarks || "",
        tech_ops_edit_summary: meta.tech_ops_edit_summary || "",
        tech_ops_modified_at: meta.tech_ops_modified_at,
        tech_ops_modified_by_name: meta.tech_ops_modified_by_name,
        reviewed_by_name: meta.reviewed_by_name,
        reviewed_at: meta.reviewed_at,
      });
      setView("wizard");
      setStep(2);
    } catch (e) {
      toast.error("Failed to load sent-back BOM for editing");
    }
  };

  // Jul 2026 — Scrap the composition of a sent-back SKU. Wipes
  // common_bom + safe-deletes any DRAFT RMs this SKU minted (skipping
  // any RM reused by another SKU's common_bom); backend flips status
  // to DRAFT_NO_BOM so the SKU code re-enters the "needs BOM" pool.
  const scrapSentBackSku = async (bidsoSkuId) => {
    if (!bidsoSkuId) return;
    if (!window.confirm(
      "Delete this SKU's BOM and DRAFT parts?\n\n" +
      "The SKU code will be retained and returned to the 'needs BOM' pool " +
      "for a fresh compose. DRAFT parts reused by another SKU stay.",
    )) return;
    try {
      const r = await axios.post(
        `${API}/bidso-composer/${encodeURIComponent(bidsoSkuId)}/scrap-and-delete`,
        {},
        { headers: authHeaders },
      );
      const deleted = (r.data?.draft_rms_deleted || []).length;
      const skipped = (r.data?.draft_rms_skipped_reused || []).length;
      toast.success(
        `${bidsoSkuId} scrapped — ${deleted} DRAFT part(s) removed`
        + (skipped ? `, ${skipped} kept (reused elsewhere)` : "")
        + ". SKU code retained.",
      );
      setSentBackInfo(null);
      setRows([]);
      setTargetId("");
      setView("landing");
      setStep(1);
      window.dispatchEvent(new Event("bidso-composer:refresh"));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to scrap SKU";
      toast.error(typeof msg === "string" ? msg : "Failed to scrap SKU");
    }
  };

  const enterWizard = (reason) => {
    setEntryReason(reason);
    if (reason === "attach") {
      setView("wizard");
      setStep(1);
      setTargetId(""); setSourceId(""); setRows([]);
      return;
    }
    if (reason === "resume") {
      // 1 draft → jump straight to Step 2. >1 → show small chooser first.
      if (liveIncompleteDrafts.length === 1) {
        resumeDraft(liveIncompleteDrafts[0].bidso_sku_id);
      } else {
        setView("resume-picker");
      }
      return;
    }
    if (reason === "recolour") {
      // Show an in-page picker of ACTIVE SKUs. User picks one to recolour.
      setView("recolour-picker");
      setTargetId(""); setSourceId(""); setRows([]);
      return;
    }
    if (reason === "sent-back") {
      // Jul 2026 — always route through the picker when >1 sent-back
      // row exists, so the designer can choose. With exactly one,
      // preserve the direct-open behavior (AC3).
      const items = liveStats.sent_back_items || [];
      if (items.length === 0) {
        toast.info("No sent-back items right now.");
        return;
      }
      if (items.length === 1) {
        loadSentBackForEdit(items[0].bidso_sku_id);
        return;
      }
      setView("sent-back-picker");
    }
  };

  // Fetch ACTIVE moulds list once (used by the "Add part" picker dialog).
  // The dummy `ALL_MOULDS` (6 hard-coded rows) is retired — the picker now
  // surfaces every mould in `db.moulds` filtered by mould_code/part_name.
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const [mouldsRes, pantonesRes] = await Promise.all([
          axios.get(`${API}/moulds`, { headers: authHeaders, params: { status: "ACTIVE" } }),
          // Pull the full pantone master (max 5000). Default backend limit is
          // 100 — without this override the composer's dropdown silently
          // missed every shade beyond the first 100 alphabetically.
          axios.get(`${API}/pantone/shades`, { headers: authHeaders, params: { limit: 5000 } }).catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        setLiveMoulds(Array.isArray(mouldsRes.data) ? mouldsRes.data : []);
        const pdata = Array.isArray(pantonesRes.data) ? pantonesRes.data : (pantonesRes.data?.items || []);
        setLivePantones(pdata.filter((p) => (p.status || "ACTIVE") === "ACTIVE"));
      } catch (e) {
        if (!cancel) { setLiveMoulds([]); setLivePantones([]); }
      }
    })();
    return () => { cancel = true; };
  }, [token, authHeaders]);

  // Pull a source SKU's real BOM, shaped into the row structure Step 2 expects.
  // `mode` controls pantone behaviour:
  //   - "blank":    INP/INM pantones cleared (attach-fresh path — Tile 1 default)
  //   - "preserve": INP/INM pantones pre-filled from current colour (recolour path — Tile 3)
  // The original source pantone is always stashed on `_source_pantone` so the
  // user can flip the Step 2 toggle and restore source colours instantly
  // without re-fetching the BOM.
  const fetchSourceBomRows = async (skuId, mode) => {
    setLoadingBom(true);
    try {
      const res = await axios.get(
        `${API}/demand-hub/bidso-skus/${encodeURIComponent(skuId)}/bom-for-clone`,
        { headers: authHeaders },
      );
      const items = res.data?.bom_items || [];
      // Build a case-insensitive lookup of master shades so we can normalise
      // each row's pantone to the master's exact casing. This makes the
      // Radix Select render its selected state (it compares value props with
      // strict equality, so casing must match the SelectItem exactly).
      const masterByLower = new Map(
        (livePantones || []).map(p => [(p.pantone_code || "").toLowerCase(), p.pantone_code])
      );
      return items.map((it) => {
        const editable = isEditable(it.category);
        const rawPantone =
          it.pantone_code ||
          (it.category_data && (it.category_data.pantone_code || it.category_data.pantone)) ||
          it.colour ||
          "";
        // If a master entry matches case-insensitively, use the master's exact
        // string. Otherwise pass through unchanged (the synthetic fallback in
        // the dropdown will display it with a "(not in master)" badge).
        const normalised = masterByLower.get((rawPantone || "").toLowerCase()) || rawPantone;
        return {
          rm_id: it.rm_id,
          category: it.category,
          // Keep the full category_data so the ACC resolver can read
          // type/model_name/specs/unit when the designer recolours an ACC row.
          category_data: it.category_data || {},
          mould_code: it.mould_code || (it.category_data && it.category_data.mould_code) || "—",
          // R-Composer-desc fix v2 (Jul 2026) — do NOT fall back to
          // `it.rm_name`. That field defaults to `rm_id` server-side
          // when `raw_materials.name` is missing (see
          // demand_hub_routes.get_bom_for_clone), and would preempt the
          // `description` step in the render chain. Empty here → JSX
          // resolves to `description` → `rm_id` as intended.
          part_name: it.part_name || (it.category_data && it.category_data.part_name) || "",
          // R-Composer-desc (Jul 2026) — full readable RM description
          // (`raw_materials.description`) so ACC rows have a name to
          // render. See fallback chain in the Step 2/3 UI.
          description: it.description || "",
          pantone: editable && mode === "blank" ? "" : normalised,
          _source_pantone: normalised,                 // stashed for toggle restore
          _source_rm_id: it.rm_id,                     // marker: row already maps to a real RM
          _source_colourant_ids: it.colourant_ids || [], // for "reused INP" submit path
          // ACC-only fallbacks for the resolver payload (in case category_data is sparse).
          _source_acc_type: (it.category_data && it.category_data.type) || "",
          _source_acc_model: (it.category_data && it.category_data.model_name) || "",
          _source_acc_specs: (it.category_data && it.category_data.specs) || "",
          // Jul 2026 (Colourway INMFAB legacy-row fix) — stash the
          // source INM's INMFAB parent + source_type so the payload
          // builders at Step-3 commit (and the dedup preview + dupe
          // check) can route INM recolours through
          // `create_inm_from_inmfab`. Emitted verbatim by
          // `demand_hub_routes.get_bom_for_clone` for every category;
          // only INM rows carry a meaningful value. When the source
          // INM genuinely has no parent, both stay falsy so the
          // "legacy row" guard toast (AC5) still fires as intended.
          _source_parent_rm_id: it.parent_rm_id || "",
          _source_source_type: it.source_type || "",
          qty: it.quantity || 1,
          unit: it.unit || "nos",
          _editable: editable,
          _origin: "cloned",
        };
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || `Could not load BOM for ${skuId}`);
      return [];
    } finally {
      setLoadingBom(false);
    }
  };

  // Tile 1 advances from Step 1 → Step 2 once target + source are picked.
  const advanceAttach = async () => {
    if (!targetId) {
      toast.error("Pick the Bidso SKU to attach BOM to");
      return;
    }
    if (!sourceId) {
      toast.error("Pick a source SKU to clone the BOM from");
      return;
    }
    // If the target is a legacy orphan (no common_bom row, status != DRAFT_NO_BOM),
    // promote it to DRAFT_NO_BOM first so the existing commit-BOM flow accepts it.
    const live = liveEmptyBomSkus.find((s) => s.bidso_sku_id === targetId);
    if (live && live._scan_origin === "legacy") {
      try {
        const res = await axios.post(
          `${API}/bidso-composer/${encodeURIComponent(targetId)}/promote-to-draft`,
          {},
          { headers: authHeaders },
        );
        const prev = res.data?.previous_status;
        if (prev) {
          toast.success(`Promoted ${targetId} from ${prev} to DRAFT_NO_BOM`);
          // Patch local cache so the preview chip reflects the new status.
          setLiveEmptyBomSkus((rows) =>
            rows.map((r) =>
              r.bidso_sku_id === targetId
                ? { ...r, status: "DRAFT_NO_BOM", _scan_origin: "draft" }
                : r,
            ),
          );
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        toast.error(`Could not promote ${targetId}: ${detail}`);
        return;
      }
    }
    setAttachColourMode("blank");                        // fresh entry — start blank
    const cloned = await fetchSourceBomRows(sourceId, "blank");
    if (cloned.length === 0) return;  // toast already fired
    setRows(cloned);
    setStep(2);
  };

  // Tile 3 (Recolour): pick an ACTIVE SKU → load its BOM with pantones **pre-filled**
  // (recolour is a tweak flow, so designers see current shades and only change what differs).
  const startRecolour = async (skuId) => {
    setEntryReason("recolour");
    setSourceId(skuId);
    setTargetId("(new SKU on submit)");
    const cloned = await fetchSourceBomRows(skuId, "preserve");
    setRows(cloned);
    setView("wizard");
    setStep(2);
  };

  // -- Step 1 → 2 transition: cloned by `advanceCreate` (create) or
  //    `resumeDraft` (resume/sent-back). No-op kept here for backward
  //    references during the mock refactor.

  const updatePantone = (idx, code) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, pantone: code } : r));
  };
  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };
  const openMouldPicker = (idx) => {
    setReplaceRowIdx(idx);
    setMouldSearch("");
    setMouldPickerOpen(true);
  };
  const pickMould = (m) => {
    setRows(prev => {
      if (replaceRowIdx === null) {
        return [...prev, {
          rm_id: "",
          category: "INP",        // every newly added mould part defaults to INP-editable
          mould_code: m.mould_code,
          part_name: m.part_name,
          pantone: "",
          qty: 1,
          _editable: true,
          _origin: "added",
        }];
      }
      return prev.map((r, i) => i === replaceRowIdx
        ? { ...r, mould_code: m.mould_code, part_name: m.part_name, pantone: "", _origin: "replaced", _editable: true, category: "INP" }
        : r
      );
    });
    setMouldPickerOpen(false);
    setReplaceRowIdx(null);
  };

  // ─── ACC picker (Add accessory) ─────────────────────────────────────────
  // Opens an empty dialog with just a search bar. Catalog is never dumped on
  // open — results materialise only after the user types ≥2 chars.
  // The added row is shaped identically to a cloned ACC so it flows through
  // the existing ACC branch in `buildPartsPayload` with NO special-casing:
  //   • pantone left as source → Issue-7 pass-through (resolves to source rm_id)
  //   • pantone changed → /raw-materials/acc/resolve-or-create
  const openAccPicker = () => {
    setAccDupNoticeFor("");
    setAccSearch("");
    setAccResults([]);
    setAccPickerOpen(true);
  };
  useEffect(() => {
    if (!accPickerOpen) return;
    const q = accSearch.trim();
    if (q.length < 2) return;     // empty/short → leave the picker on its empty-state row
    let cancel = false;
    setAccSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/raw-materials`,
          { headers: authHeaders, params: { search: q } },
        );
        if (cancel) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const filtered = list
          .filter((rm) =>
            rm.category === "ACC" &&
            (rm.status || "ACTIVE") !== "INACTIVE" &&
            (rm.status || "ACTIVE") !== "MERGED"
          )
          .slice(0, 200);
        setAccResults(filtered);
      } catch (_e) {
        if (!cancel) setAccResults([]);
      } finally {
        if (!cancel) setAccSearching(false);
      }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [accPickerOpen, accSearch, authHeaders]);

  const pickAcc = (acc) => {
    if (!acc || !acc.rm_id) return;
    if (rows.some((r) => r.rm_id === acc.rm_id)) {
      setAccDupNoticeFor(acc.rm_id);
      return;
    }
    const cd = acc.category_data || {};
    const pantone = cd.pantone_code || cd.pantone || "";
    const uom = acc.resolved_uom || acc.procurement_uom || acc.uom || "nos";
    const displayName = cd.model_name
      ? `${cd.model_name}${cd.type ? " · " + cd.type : ""}`
      : (acc.description || acc.rm_id);
    setRows((prev) => [...prev, {
      rm_id: acc.rm_id,
      category: "ACC",
      category_data: {
        type: cd.type || "",
        model_name: cd.model_name || "",
        specs: cd.specs || "",
        unit: uom,
      },
      part_name: displayName,
      pantone,
      _source_rm_id: acc.rm_id,
      _source_pantone: pantone,
      _source_acc_type: cd.type || "",
      _source_acc_model: cd.model_name || "",
      _source_acc_specs: cd.specs || "",
      qty: 1,
      unit: uom,
      _editable: true,
      _origin: "added",
    }]);
    setAccPickerOpen(false);
    setAccDupNoticeFor("");
  };

  // -- Step 2 LIVE dedup preview --------------------------------------
  // Debounced POST to /bidso-composer/preview-row-dedup whenever the
  // editable rows change. Single source of truth = backend
  // `find_duplicate_rm` (same function commit-bom calls), so the Step 2
  // badge can never disagree with what commit will actually do.
  //
  // We send only enough metadata per row to dedup:
  //   • INP/INM → mould_id (resolved from mould_code) + pantone_code
  //   • ACC     → type/model_name/specs + pantone_code, plus the cloned
  //               source rm_id / source pantone so unchanged-pantone rows
  //               resolve to the source instantly without a DB scan.
  useEffect(() => {
    // Only fire when we have the moulds catalog loaded (else mould_id
    // resolution would silently return null for every row).
    if (!liveMoulds || liveMoulds.length === 0) return;
    const editableRows = rows.map((r, idx) => ({ r, idx }))
      .filter(({ r }) => r._editable && r.pantone);
    // Bump the req id so any in-flight fetch from a previous render is
    // ignored when it resolves — prevents a stale response from
    // overwriting fresh state.
    const reqId = dedupReqIdRef.current + 1;
    dedupReqIdRef.current = reqId;
    // Defer the "checking…" flip one tick — the lint rule treats a
    // direct setState in useEffect as a smell. The user-visible effect
    // is identical (1ms before debounce timer starts).
    const checkingFlip = setTimeout(() => {
      if (dedupReqIdRef.current === reqId && editableRows.length > 0) {
        setDedupChecking(true);
      }
    }, 0);
    const handle = setTimeout(async () => {
      if (editableRows.length === 0) {
        if (dedupReqIdRef.current === reqId) {
          setDedupPreviews({});
          setDedupChecking(false);
        }
        return;
      }
      const payloadRows = editableRows.map(({ r, idx }) => {
        const cat = (r.category || "INP").toUpperCase();
        if (cat === "ACC") {
          return {
            idx,
            category: "ACC",
            type: r.category_data?.type || r.acc_type || r._source_acc_type || "",
            model_name: r.category_data?.model_name || r._source_acc_model || r.part_name || "",
            specs: r.category_data?.specs || r._source_acc_specs || "",
            pantone_code: r.pantone || "",
            source_rm_id: r.rm_id || r._source_rm_id || null,
            source_pantone_code: r._source_pantone || null,
          };
        }
        // INM (Feb 2026 INMFAB build) — dedup by (parent_rm_id, pantone),
        // NOT by mould_id. Legacy INMs without a parent stamp fall
        // through to the mould-code identity below.
        if (cat === "INM" && r._source_parent_rm_id) {
          return {
            idx,
            category: "INM",
            parent_rm_id: r._source_parent_rm_id,
            source_type: r._source_source_type || "MANUFACTURED",
            pantone_code: r.pantone || "",
          };
        }
        const mould = (liveMoulds || []).find((x) => x.mould_code === r.mould_code);
        return {
          idx,
          category: cat,
          mould_id: mould?.id || mould?.mould_id || null,
          pantone_code: r.pantone || "",
        };
      });
      try {
        const res = await axios.post(
          `${API}/bidso-composer/preview-row-dedup`,
          { rows: payloadRows },
          { headers: authHeaders },
        );
        // Race-guard: only commit state from the LATEST request.
        if (dedupReqIdRef.current !== reqId) return;
        const map = {};
        for (const item of (res.data?.results || [])) {
          map[item.idx] = item;
        }
        setDedupPreviews(map);
        setDedupChecking(false);
      } catch (e) {
        if (dedupReqIdRef.current !== reqId) return;
        // Never block the UI on a preview failure — the badge just falls
        // back to "new" until the next debounce hit, BUT we still clear
        // the checking flag so the user sees the fallback state.
        setDedupPreviews({});
        setDedupChecking(false);
      }
    }, 350);
    return () => {
      clearTimeout(checkingFlip);
      clearTimeout(handle);
    };
  }, [rows, liveMoulds, authHeaders]);

  // Point 7 / shared helper (Feb 2026):
  // Editable rows whose pantone is unchanged from the source are
  // pre-approved — the same rm_id resolves, no master-library check, no
  // resolver call, no dedup re-run. Returning true short-circuits every
  // gate that would otherwise reject a legacy free-text pantone (e.g.
  // "Green" inherited from `category_data.colour`).
  //
  // Permissive on `_origin` (covers "cloned", "sent-back" resubmits, and
  // in-place edits) — only requires `_source_rm_id` to be set so the
  // shortcut has somewhere to resolve to.
  //
  // Used by: `missingPantone`, `newInps/existingInps` split,
  // `rowsWithUnknownPantone`, the row badge, and the ACC branch in
  // `buildPartsPayload`. If you add another gate that reads `r.pantone`,
  // gate it through this.
  const isSourceUnchanged = (r) => {
    if (!r) return false;
    if (!r._source_rm_id) return false;
    const srcP = (r._source_pantone || "").trim().toLowerCase();
    const curP = (r.pantone || "").trim().toLowerCase();
    return curP === srcP;
  };

  const review = useMemo(() => {
    // Issue-7 fix: an editable ACC whose pantone is unchanged from its
    // source AND has a resolved rm_id is "fine as-is" — even when both
    // its current and source pantones are empty (hardware / fasteners /
    // no-pantone parts). The missing-pantone banner must only flag rows
    // that are GENUINELY new (no source rm_id) or whose pantone WAS set
    // but got cleared (data-loss attempt).
    const missingPantone = rows.filter(r => {
      if (!r._editable || r.pantone) return false;
      // No-source row → must have a pantone.
      if (!r._source_rm_id && !r.rm_id) return true;
      // Source had a pantone but designer cleared it → flag.
      const srcPantone = (r._source_pantone || "").trim();
      return srcPantone.length > 0;
    });
    const newInps = [];
    const existingInps = [];
    rows.forEach((r, idx) => {
      if (!r._editable) return;
      // Source-unchanged ACC rows resolve to their original rm_id without
      // hitting the dedup API — same shortcut the badge uses.
      const sourceUnchanged = isSourceUnchanged(r);
      const preview = dedupPreviews[idx];
      const matchedRm = sourceUnchanged
        ? r._source_rm_id
        : (preview?.action === "existing" ? preview.rm_id : null);
      if (matchedRm) {
        existingInps.push({ ...r, _matched_rm: matchedRm });
      } else if (r.pantone) {
        newInps.push({ ...r });
      }
    });
    // CRITICAL (Feb 2026 fix): the old heuristic that flipped
    // `fakeDuplicateOf` whenever every editable row matched an existing
    // rm_id is gone — that's a NECESSARY but not SUFFICIENT condition for
    // a real duplicate, and it produced false positives that blocked
    // legitimate submits. The real check now lives in
    // `useDuplicateBomCheck` below: it calls
    // `POST /bidso-composer/check-duplicate-bom`, which runs the same
    // canonical-signature check commit-bom would, and returns the actual
    // matching `bidso_sku_id` (or null).

    return { missingPantone, newInps, existingInps };
  }, [rows, dedupPreviews, sentBackInfo]);

  // Editable rows whose pantone isn't in the live pantone master — submit
  // would be rejected server-side with NO_MATCHING_COLOURANT, so we block the
  // submit button up-front with a clear inline reason.
  // Point 7 (Feb 2026): unchanged-source rows are pre-approved via
  // `isSourceUnchanged` and skipped here too (legacy free-text pantones
  // like "Green" on cloned ACCs must not block submit).
  const rowsWithUnknownPantone = useMemo(() => {
    const set = new Set((livePantones || []).map(p => (p.pantone_code || "").toLowerCase()));
    return rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) =>
        r._editable &&
        r.pantone &&
        !set.has((r.pantone || "").toLowerCase()) &&
        !isSourceUnchanged(r)
      )
      .map(({ r, idx }) => ({ idx: idx + 1, mould_code: r.mould_code, pantone: r.pantone }));
  }, [rows, livePantones]);

  // ----- Submit for Tech Ops review (real backend) -----
  const [submitting, setSubmitting] = useState(false);
  const [submittedInfo, setSubmittedInfo] = useState(null);   // { bidso_sku_id, name, new_inps, reused_inps }
  const [myPendingReviews, setMyPendingReviews] = useState([]); // populated post-submit
  const [submitError, setSubmitError] = useState(null);   // sticky inline error (NO_MATCHING_COLOURANT / DUPLICATE_BOM)
  // Optional free-text note from the designer to Tech Ops, attached to the
  // commit-bom POST and surfaced in the Tech Ops review queue. Capped at
  // 2000 chars to match the backend Pydantic validator.
  const [submitterRemarks, setSubmitterRemarks] = useState("");

  // ─── Real duplicate-BOM check (Feb 2026) ───────────────────────────────
  // Replaces the old client-side heuristic that flipped a banner whenever
  // every editable row resolved to an existing rm_id (necessary but NOT
  // sufficient for a real duplicate — blocked legit submits). Calls the
  // read-only `/bidso-composer/check-duplicate-bom` endpoint when Step 3
  // mounts (and re-runs when rows or dedup previews change). Result:
  //   { loading, match: null | {matched_bidso_sku_id, status, description, vertical_code, model_name}, skipped: boolean, reason }
  const [dupCheck, setDupCheck] = useState({ loading: false, match: null, skipped: false, reason: "" });

  // Build a lightweight check payload directly from rows. NO side-effects
  // (we DON'T call the ACC resolver here — that would mint ACCs on a
  // preview). Skips the check when any row would need minting (new ACC
  // pantone, new INP colour) because a brand-new rm_id can't collide
  // with an ACTIVE common_bom anyway.
  const buildCheckPayload = () => {
    const parts = [];
    let skipReason = "";
    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      if (r._editable && (r.category === "INP" || r.category === "INM")) {
        // INM (Feb 2026 INMFAB build) — send parent_rm_id-keyed part,
        // not mould-keyed.
        if (r.category === "INM" && r._source_parent_rm_id) {
          if (!r.pantone) {
            skipReason = "incomplete-row";
            return { parts: null, skipReason };
          }
          parts.push({
            category: "INM",
            parent_rm_id: r._source_parent_rm_id,
            source_type: r._source_source_type || "MANUFACTURED",
            pantone_code: r.pantone,
            quantity: Number(r.qty) || 1,
          });
          continue;
        }
        const mouldId = resolveMouldId(r.mould_code);
        if (!mouldId || !r.pantone) {
          // Row incomplete — defer the check.
          skipReason = "incomplete-row";
          return { parts: null, skipReason };
        }
        parts.push({ mould_id: mouldId, pantone_code: r.pantone, quantity: Number(r.qty) || 1 });
        continue;
      }
      if (r._editable && r.category === "ACC") {
        // Unchanged source pantone → pass-through to source rm_id.
        if (isSourceUnchanged(r) && (r._source_rm_id || r.rm_id)) {
          parts.push({ rm_id: r._source_rm_id || r.rm_id, quantity: Number(r.qty) || 1 });
          continue;
        }
        // Pantone-changed or new ACC → would mint a (possibly) brand-new
        // rm_id on commit; the dedup preview already tells us if it
        // resolves to an existing one.
        const previewed = dedupPreviews[idx]?.rm_id;
        if (previewed) {
          parts.push({ rm_id: previewed, quantity: Number(r.qty) || 1 });
          continue;
        }
        skipReason = "new-acc-pending";
        return { parts: null, skipReason };
      }
      // Non-editable / hardware / SP: pass rm_id through.
      if (r.rm_id) {
        parts.push({ rm_id: r.rm_id, quantity: Number(r.qty) || 1 });
      }
    }
    return { parts, skipReason };
  };

  const runDuplicateCheck = useCallback(async () => {
    if (step !== 3) return;
    // Pre-condition: if the local review summary already flags new INPs,
    // the BOM signature can't match an ACTIVE bidso (the new INPs don't
    // exist yet). Skip the network call.
    if (review.newInps && review.newInps.length > 0) {
      setDupCheck({ loading: false, match: null, skipped: true, reason: "new-inps" });
      return;
    }
    const { parts, skipReason } = buildCheckPayload();
    if (!parts) {
      setDupCheck({ loading: false, match: null, skipped: true, reason: skipReason });
      return;
    }
    setDupCheck((d) => ({ ...d, loading: true }));
    try {
      const res = await axios.post(
        `${API}/bidso-composer/check-duplicate-bom`,
        { parts, bidso_sku_id: targetId || null },
        { headers: authHeaders },
      );
      const data = res.data || {};
      if (data.matched_bidso_sku_id) {
        setDupCheck({ loading: false, match: data, skipped: false, reason: "" });
      } else {
        setDupCheck({ loading: false, match: null, skipped: false, reason: data.reason || "" });
      }
    } catch (_e) {
      // On error, don't block submit — surface the network failure
      // silently; commit-bom's own 409 will be the final gate.
      setDupCheck({ loading: false, match: null, skipped: true, reason: "check-failed" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rows, dedupPreviews, review.newInps, targetId, authHeaders]);

  useEffect(() => { runDuplicateCheck(); }, [runDuplicateCheck]);

  // Resolve mould_id from mould_code via the loaded liveMoulds list. Returns
  // null if no match — caller surfaces a clear toast.
  const resolveMouldId = (mouldCode) => {
    if (!mouldCode || mouldCode === "—") return null;
    const m = (liveMoulds || []).find((x) => x.mould_code === mouldCode);
    return m?.id || m?.mould_id || null;
  };

  // Build the commit-bom parts payload from rows. Returns { parts, errors }.
  // Build the commit-bom parts payload. For ACC rows where the designer
  // changed the pantone, calls `/raw-materials/acc/resolve-or-create` so the
  // server dedups against existing ACCs by (type, model_name, specs,
  // pantone_code) and either returns an existing rm_id or creates a new one.
  // Returns { parts, errors, accResolutions: [{from, to, action}] }.
  const buildPartsPayload = async () => {
    const parts = [];
    const errors = [];
    const accResolutions = [];
    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      const rowLabel = `Row ${idx + 1} (${r.mould_code || r.rm_id || "?"})`;
      // INM — Feb 2026 INMFAB build. The composer now MINTS/REUSES INMs
      // via `create_inm_from_inmfab` — the source INM's `parent_rm_id`
      // + `source_type` are carried over so the new colour re-derives
      // the PWD + INMFAB recipe. Legacy INMs without a `parent_rm_id`
      // stamp still fall through to the unchanged-source pass-through
      // (Q2(a) — no mould→INMFAB heuristic).
      if (r._editable && r.category === "INM") {
        const parentRmId = r._source_parent_rm_id || "";
        const sourceType = r._source_source_type || "MANUFACTURED";
        // Unchanged-source pass-through — cheap identity match.
        if (isSourceUnchanged(r) && (r._source_rm_id || r.rm_id)) {
          parts.push({
            rm_id: r._source_rm_id || r.rm_id,
            quantity: Number(r.qty) || 1,
            unit: r.unit || "nos",
          });
          continue;
        }
        // Recolour path — requires an INMFAB parent stamped on the
        // source row. Legacy INMs without one can't be recoloured
        // through this build.
        if (!parentRmId) {
          const rmId = r._source_rm_id || r.rm_id;
          if (rmId && isSourceUnchanged(r)) {
            parts.push({ rm_id: rmId, quantity: Number(r.qty) || 1, unit: r.unit || "nos" });
            continue;
          }
          errors.push(
            `${rowLabel}: this INM row has no INMFAB parent stamped (legacy row). ` +
            `Restore the original pantone or ask Tech Ops to link an INMFAB parent first.`
          );
          continue;
        }
        if (!r.pantone) {
          errors.push(`${rowLabel}: pantone is required to recolour this INM.`);
          continue;
        }
        // Send the INMFAB-driven payload — commit_bom routes this to
        // `create_inm_from_inmfab`, dedup on (parent_rm_id, colourant).
        parts.push({
          category: "INM",
          parent_rm_id: parentRmId,
          pantone_code: r.pantone,
          source_type: sourceType,
          quantity: Number(r.qty) || 1,
          unit: r.unit || "nos",
        });
        continue;
      }
      // INP — mould-derived; resolve via mould + pantone server-side.
      if (r._editable && r.category === "INP") {
        const mouldId = resolveMouldId(r.mould_code);
        if (!mouldId) {
          errors.push(`${rowLabel}: mould "${r.mould_code}" not found in master.`);
          continue;
        }
        if (!r.pantone) {
          errors.push(`${rowLabel}: pantone is required for editable parts.`);
          continue;
        }
        parts.push({
          mould_id: mouldId,
          pantone_code: r.pantone,
          quantity: Number(r.qty) || 1,
          unit: r.unit || "nos",
        });
        continue;
      }
      // ACC editable — resolve via (type, model, specs, pantone) server-side
      if (r._editable && r.category === "ACC") {
        // Feb 2026 Point 3 / FIX 2b + Point 7 dedup consolidation
        // Order matters here:
        //   1. PASS-THROUGH for unchanged source (covers colour-less ACCs
        //      like hardware/fasteners whose source has no pantone, AND
        //      legacy ACCs whose source pantone is a free-text colour
        //      like "Green" — Point 7).
        //   2. Pantone-required hard reject (only for genuinely-new ACCs).
        //   3. Backend resolver call when dedup preview signals action="new"
        //      or the resolved rm_id differs from source — NEVER silently
        //      reuse the old rm_id when type/model_name are missing.
        const srcRmId = r._source_rm_id || r.rm_id || null;

        // 1. Unchanged-source pass-through (shared helper, covers both
        // both-empty and same-string-pantone cases).
        if (isSourceUnchanged(r) && srcRmId) {
          parts.push({
            rm_id: srcRmId,
            quantity: Number(r.qty) || 1,
            unit: r.unit || "nos",
          });
          continue;
        }

        // 2. Pantone required for genuinely-new or pantone-edited ACCs
        if (!r.pantone) {
          errors.push(`${rowLabel}: pantone is required for new/edited ACC parts.`);
          continue;
        }

        // 3. Resolve via backend — dedup preview is the source of truth.
        const preview = dedupPreviews[idx];
        const previewedRmId = preview?.rm_id || null;
        const previewAction = preview?.action;
        const resolverNeeded =
          previewAction === "new" ||
          (previewedRmId && srcRmId && previewedRmId !== srcRmId) ||
          !preview; // preview not yet returned → call resolver (idempotent)

        if (!resolverNeeded && previewedRmId) {
          parts.push({
            rm_id: previewedRmId,
            quantity: Number(r.qty) || 1,
            unit: r.unit || "nos",
          });
          continue;
        }

        try {
          const accBody = {
            type: r.category_data?.type || r.acc_type || r._source_acc_type || "",
            model_name: r.category_data?.model_name || r._source_acc_model || r.part_name || "",
            specs: r.category_data?.specs || r._source_acc_specs || "",
            pantone_code: r.pantone,
            source_rm_id: srcRmId,
          };
          // Hydrate missing identity from the source ACC (legacy rows
          // that didn't carry category_data hints).
          if ((!accBody.type || !accBody.model_name) && srcRmId) {
            try {
              const srcRes = await axios.get(
                `${API}/raw-materials?rm_id=${encodeURIComponent(srcRmId)}`,
                { headers: authHeaders },
              );
              const srcDoc = Array.isArray(srcRes.data) ? srcRes.data[0] : (srcRes.data?.items?.[0] || null);
              const srcCd = srcDoc?.category_data || {};
              if (!accBody.type) accBody.type = srcCd.type || "";
              if (!accBody.model_name) accBody.model_name = srcCd.model_name || "";
              if (!accBody.specs) accBody.specs = srcCd.specs || "";
            } catch (_) { /* silent — handled by the check below */ }
          }
          if (!accBody.type || !accBody.model_name) {
            // Never silently reuse the old rm_id — surface the error
            // so the designer fixes the ACC identity.
            errors.push(`${rowLabel}: Cannot resolve ACC identity — set type/model_name.`);
            continue;
          }
          const res = await axios.post(
            `${API}/raw-materials/acc/resolve-or-create`,
            accBody,
            { headers: authHeaders },
          );
          const resolvedRmId = res.data?.rm_id;
          if (!resolvedRmId) {
            errors.push(`${rowLabel}: ACC resolver returned no rm_id.`);
            continue;
          }
          accResolutions.push({
            from: r.rm_id || "(new)",
            to: resolvedRmId,
            action: res.data?.action,
            pantone: r.pantone,
          });
          parts.push({
            rm_id: resolvedRmId,
            quantity: Number(r.qty) || 1,
            unit: r.unit || "nos",
          });
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (d?.code === "PANTONE_NOT_IN_MASTER") {
            errors.push(`${rowLabel}: ${d.message}`);
          } else {
            errors.push(`${rowLabel}: ACC resolver failed (${d?.message || e?.message || "unknown"})`);
          }
        }
        continue;
      }
      // Non-editable (legacy SP, hardware) — pass rm_id through unchanged
      if (!r.rm_id) {
        errors.push(`${rowLabel}: missing rm_id for non-editable part.`);
        continue;
      }
      parts.push({
        rm_id: r.rm_id,
        quantity: Number(r.qty) || 1,
        unit: r.unit || "nos",
      });
    }
    return { parts, errors, accResolutions };
  };

  // Fetch the user's own pending-review SKUs for the post-submit confirmation
  // screen. Filters /tech-ops/requests client-side to PENDING_REVIEW + submitted_by==me.
  const refreshMyPendingReviews = async () => {
    try {
      const res = await axios.get(`${API}/bidso-composer/tech-ops/requests`, { headers: authHeaders });
      const me = res.data?.current_user_id;
      const mine = (res.data?.items || []).filter(
        (r) => r.status === "PENDING_REVIEW" && r.submitted_by === me,
      );
      setMyPendingReviews(mine);
    } catch (e) {
      setMyPendingReviews([]);
    }
  };

  const handleSubmitForReview = async () => {
    if (submitting) return;
    // Gate on dedup preview completion. If the user clicks Submit while
    // previews are still in flight (debounce hasn't settled or POST is
    // pending), we'd be committing without knowing whether dedup will
    // mint a fresh INP or reuse — exactly the duplicate-INP failure the
    // user called out. Block submit, surface a one-shot toast, and let
    // the next render (when checking flips false) re-enable.
    if (dedupChecking) {
      toast.error("Dedup preview still resolving — wait a moment, then try again.");
      return;
    }
    setSubmitError(null);
    const { parts, errors, accResolutions } = await buildPartsPayload();
    if (errors.length) {
      errors.slice(0, 3).forEach((m) => toast.error(m));
      return;
    }
    if (parts.length === 0) {
      toast.error("Add at least one part before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      // Path 3 (recolour) — no real target SKU yet. Create a fresh draft
      // sibling under the source's vertical/model/license first, then commit.
      let bidsoSkuId = targetId;
      let createdName = null;
      if (entryReason === "recolour") {
        const src = (liveActiveSkus || []).find((s) => s.bidso_sku_id === sourceId);
        if (!src || !src.vertical_id || !src.model_id) {
          throw new Error(
            "Could not resolve source SKU's vertical/model for the new variant. Refresh and try again.",
          );
        }
        const draftRes = await axios.post(
          `${API}/bidso-composer/draft`,
          {
            vertical_id: src.vertical_id,
            model_id: src.model_id,
            license_code: src.license_code || "",
            source_bidso_sku_id: sourceId,
            name: src.name ? `${src.name} (variant)` : "",
          },
          { headers: authHeaders },
        );
        bidsoSkuId = draftRes.data?.bidso_sku?.bidso_sku_id || draftRes.data?.bidso_sku_id;
        createdName = draftRes.data?.bidso_sku?.name;
        if (!bidsoSkuId) throw new Error("Draft creation returned no bidso_sku_id");
      }
      const res = await axios.post(
        `${API}/bidso-composer/${encodeURIComponent(bidsoSkuId)}/commit-bom`,
        { parts, submitter_remarks: submitterRemarks.trim() || null },
        { headers: authHeaders },
      );
      // Success: surface confirmation screen.
      setSubmittedInfo({
        bidso_sku_id: bidsoSkuId,
        name: createdName || target?.name || "",
        new_inps: res.data?.new_inps_created || [],
        reused_inps: res.data?.existing_inps_reused || [],
        items_count: res.data?.items_count || parts.length,
        acc_resolutions: accResolutions || [],
      });
      setView("submitted");
      // Clear the remarks textarea once the SKU has moved into PENDING_REVIEW
      // so the next compose session starts blank.
      setSubmitterRemarks("");
      await refreshMyPendingReviews();
      // Tell the parent (SKUManagement, other tabs) to refresh — drops the
      // just-submitted SKU out of any DRAFT_NO_BOM lists immediately.
      window.dispatchEvent(new Event("bidso-composer:refresh"));
      // Summarise ACC resolutions in the toast so the designer instantly sees
      // how many ACCs were reused vs created.
      const accCreated = (accResolutions || []).filter((a) => a.action === "created").length;
      const accMatched = (accResolutions || []).filter((a) => a.action === "matched" || a.action === "reactivated").length;
      const accSuffix = (accCreated + accMatched) > 0
        ? ` · ACCs: ${accMatched} matched, ${accCreated} created`
        : "";
      toast.success(`${bidsoSkuId} submitted for Tech Ops review${accSuffix}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "DUPLICATE_BOM") {
        const msg = `Duplicate BOM — this exact set of parts already exists as ${d.matched_bidso_sku_id}.`;
        setSubmitError({ kind: "DUPLICATE_BOM", message: msg, matched_bidso_sku_id: d.matched_bidso_sku_id });
        toast.error(msg, { duration: 8000 });
      } else if (d?.code === "NO_MATCHING_COLOURANT") {
        setSubmitError({
          kind: "NO_MATCHING_COLOURANT",
          message: d.message,
          mould_code: d.mould_code,
          pantone_code: d.pantone_code,
        });
        toast.error(d.message, { duration: 10000 });
      } else if (d?.code === "SUB_MOULD_INHERITS_FROM_PARENT" || e.originalDetail?.code === "SUB_MOULD_INHERITS_FROM_PARENT") {
        // Feb 2026 — Family Sub-Moulds guard fired on the backend.
        const od = e.originalDetail || d;
        setSubmitError({
          kind: "ERROR",
          message: `${od.message} Edit the parent mould directly; sub-moulds inherit these fields.`,
        });
        toast.error(od.message, { duration: 10000 });
      } else if ((typeof d === "string" && d.toLowerCase().includes("family parent"))
                 || (typeof e.originalDetail === "string" && e.originalDetail.toLowerCase().includes("family parent"))) {
        // Feb 2026 — parent-with-sub-moulds is blocked from INP tagging.
        // Guide the user to pick a specific sub-mould instead.
        const raw = (typeof e.originalDetail === "string" ? e.originalDetail : d);
        setSubmitError({
          kind: "ERROR",
          message: `${raw} Change the mould_code on this row to point at a specific sub-mould (e.g. "PARENT/1").`,
        });
        toast.error(raw, { duration: 10000 });
      } else if (typeof d === "string") {
        setSubmitError({ kind: "ERROR", message: d });
        toast.error(d);
      } else if (d?.message) {
        setSubmitError({ kind: "ERROR", message: d.message });
        toast.error(d.message);
      } else {
        const msg = e?.message || "Submit failed";
        setSubmitError({ kind: "ERROR", message: msg });
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="bidso-composer-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Palette className="w-8 h-8 text-violet-600" />
            Bidso SKU Composer
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {view === "landing"
              ? "Create new Bidso SKU codes, attach BOMs, and submit for Tech Ops approval."
              : "BOM attachment & colour composition flow."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {view !== "landing" && (
            <Button variant="ghost" size="sm" onClick={() => { setView("landing"); setSentBackInfo(null); }} data-testid="back-to-landing">
              <ArrowLeft className="w-4 h-4 mr-1"/>Back to landing
            </Button>
          )}
          {view === "wizard" && target && <Badge variant="secondary" className="font-mono">{target.bidso_sku_id}</Badge>}
        </div>
      </div>

      {/* ===================== LANDING ===================== */}
      {view === "landing" && (
        <div className="space-y-6" data-testid="landing">
          {/* Sent-back banner — only shows when there's work returned by Tech Ops */}
          {liveStats.sent_back_count > 0 && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="sent-back-banner">
              <MessageSquareWarning className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-semibold text-red-900">
                  {liveStats.sent_back_count} bidso SKU sent back by Tech Ops with remarks
                </div>
                <ul className="text-sm text-red-800 mt-1 space-y-0.5">
                  {(liveStats.sent_back_items || []).map(it => (
                    <li key={it.bidso_sku_id} className="flex items-start gap-2">
                      <Badge variant="outline" className="font-mono">{it.bidso_sku_id}</Badge>
                      <span>{it.review_remarks}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button size="sm" variant="destructive" onClick={() => enterWizard("sent-back")} data-testid="fix-sent-back-btn">
                Fix &amp; resubmit →
              </Button>
            </div>
          )}

          {/* Three primary path cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="path-cards">
            {/* Tile 1 — Attach BOM to existing SKU */}
            <Card className="border-violet-200 hover:border-violet-400 hover:shadow-md transition cursor-pointer" onClick={() => enterWizard("attach")} data-testid="card-attach-bom">
              <CardHeader className="space-y-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-violet-600"/>
                </div>
                <div>
                  <CardTitle className="text-base">Attach BOM to Existing Bidso SKU</CardTitle>
                  <Badge variant="outline" className="text-[10px] mt-1">Path 1</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Pick a Bidso SKU that already has a code but no BOM yet. Import the recipe by cloning from an ACTIVE source SKU, then assign pantones.
                </p>
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs font-semibold text-violet-700" data-testid="empty-bom-count">
                    {liveEmptyBomSkus.length} SKU{liveEmptyBomSkus.length !== 1 && "s"} need BOM
                  </span>
                  <ArrowRight className="w-4 h-4 text-violet-600"/>
                </div>
              </CardContent>
            </Card>

            {/* Path 1 cont'd — Resume Draft */}
            <Card
              className={`border-amber-200 ${liveStats.incomplete_count > 0 ? "hover:border-amber-400 hover:shadow-md cursor-pointer" : "opacity-60"} transition`}
              onClick={() => liveStats.incomplete_count > 0 && enterWizard("resume")}
              data-testid="card-resume"
            >
              <CardHeader className="space-y-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-amber-600"/>
                </div>
                <div>
                  <CardTitle className="text-base">Resume Incomplete Draft</CardTitle>
                  <Badge variant="outline" className="text-[10px] mt-1">Path 1 cont&apos;d</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Continue (or delete) a bidso SKU that has a code but no BOM yet.
                </p>
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs font-semibold text-amber-700" data-testid="incomplete-count">
                    {liveStats.incomplete_count} draft{liveStats.incomplete_count !== 1 && "s"} waiting
                  </span>
                  <ArrowRight className="w-4 h-4 text-amber-600"/>
                </div>
              </CardContent>
            </Card>

            {/* Path 3 — Recolour existing */}
            <Card
              className="border-emerald-200 hover:border-emerald-400 hover:shadow-md cursor-pointer transition"
              onClick={() => enterWizard("recolour")}
              data-testid="card-recolour"
            >
              <CardHeader className="space-y-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Edit2 className="w-5 h-5 text-emerald-600"/>
                </div>
                <div>
                  <CardTitle className="text-base">Recolour Existing SKU</CardTitle>
                  <Badge variant="outline" className="text-[10px] mt-1">Path 3</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Tweak pantone shades on an ACTIVE SKU. Creates a new bidso SKU when colours differ from any existing recipe.
                </p>
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs text-muted-foreground" data-testid="active-count">
                    {liveStats.active_count} ACTIVE SKUs available
                  </span>
                  <ArrowRight className="w-4 h-4 text-emerald-600"/>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ===================== RECOLOUR PICKER (Tile 3) ===================== */}
      {view === "recolour-picker" && (
        <Card data-testid="recolour-picker">
          <CardHeader>
            <CardTitle className="text-lg">Pick an ACTIVE Bidso SKU to recolour</CardTitle>
            <CardDescription>
              The current pantones will load pre-filled — tweak only the rows you want to change. A new bidso SKU is created on submit if any pantone differs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
              <Input
                value={recolourSearch}
                onChange={(e) => setRecolourSearch(e.target.value)}
                placeholder="Search by Bidso SKU id or name…"
                className="pl-9"
                data-testid="recolour-search"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              {liveActiveSkus.map(s => (
                <div key={s.bidso_sku_id} className="flex items-center justify-between border rounded-md p-3 hover:border-emerald-400 transition" data-testid={`recolour-row-${s.bidso_sku_id}`}>
                  <div>
                    <div className="font-mono font-semibold">{s.bidso_sku_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.name || `${s.vertical_code || ""} ${s.model_code || ""}`}{s.license_code ? ` · ${s.license_code}` : ""}
                    </div>
                  </div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => startRecolour(s.bidso_sku_id)}>
                    Recolour <ArrowRight className="w-4 h-4 ml-1"/>
                  </Button>
                </div>
              ))}
              {liveActiveSkus.length === 0 && (
                <div className="p-8 text-center text-zinc-500 text-sm" data-testid="recolour-empty">
                  {recolourSearch ? `No ACTIVE Bidso SKU matches "${recolourSearch}".` : "Loading…"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===================== RESUME PICKER (>1 drafts) ===================== */}
      {view === "resume-picker" && (
        <Card data-testid="resume-picker">
          <CardHeader>
            <CardTitle className="text-lg">Pick a draft to resume</CardTitle>
            <CardDescription>{liveIncompleteDrafts.length} Bidso SKUs have partial composing work saved.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {liveIncompleteDrafts.map(d => (
              <div key={d.bidso_sku_id} className="flex items-center justify-between border rounded-md p-3 hover:border-amber-400 transition" data-testid={`resume-row-${d.bidso_sku_id}`}>
                <div>
                  <div className="font-mono font-semibold">{d.bidso_sku_id}</div>
                  <div className="text-xs text-muted-foreground">{d.name || "(no name)"}{d.updated_at ? ` · last saved ${new Date(d.updated_at).toLocaleString()}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Button size="sm" onClick={() => resumeDraft(d.bidso_sku_id)}>
                    Resume <ArrowRight className="w-4 h-4 ml-1"/>
                  </Button>
                </div>
              </div>
            ))}
            {liveIncompleteDrafts.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">No drafts with saved progress.</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------- Sent-back picker (Jul 2026) ----------
          Fix & resubmit routes here whenever >1 SKU is waiting. Each
          row shows Tech Ops remarks + a BOM-presence chip so the
          designer can pick which SKU to fix vs which to scrap. */}
      {view === "sent-back-picker" && (
        <Card data-testid="sent-back-picker" className="border-red-200">
          <CardHeader>
            <CardTitle className="text-lg text-red-900">Pick a sent-back SKU to fix</CardTitle>
            <CardDescription>
              {(liveStats.sent_back_items || []).length} Bidso SKUs are waiting for the design team.
              SKUs with a loaded BOM open in the fix flow; empty ones can be scrapped &amp; recomposed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(liveStats.sent_back_items || []).map(sb => {
              const count = sb.items_count || 0;
              const hasBom = !!sb.has_bom;
              return (
                <div
                  key={sb.bidso_sku_id}
                  className="flex items-start justify-between gap-3 border rounded-md p-3 hover:border-red-400 transition"
                  data-testid={`sent-back-row-${sb.bidso_sku_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold">{sb.bidso_sku_id}</span>
                      {sb.name && (
                        <span className="text-xs text-muted-foreground">· {sb.name}</span>
                      )}
                      {hasBom ? (
                        <Badge
                          variant="outline"
                          className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]"
                          data-testid={`sent-back-bom-chip-${sb.bidso_sku_id}`}
                        >
                          BOM: {count} part{count === 1 ? "" : "s"}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-red-300 text-red-700 bg-red-50 text-[10px]"
                          data-testid={`sent-back-bom-chip-${sb.bidso_sku_id}`}
                        >
                          Empty BOM
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-red-800 mt-1 whitespace-pre-wrap">
                      <span className="font-semibold">Remarks:</span>{" "}
                      {sb.review_remarks || "(none provided)"}
                    </div>
                    {sb.reviewed_at && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Sent back {new Date(sb.reviewed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => loadSentBackForEdit(sb.bidso_sku_id)}
                      disabled={!hasBom}
                      data-testid={`sent-back-open-${sb.bidso_sku_id}`}
                    >
                      {hasBom ? "Open" : "No BOM"} <ArrowRight className="w-4 h-4 ml-1"/>
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => scrapSentBackSku(sb.bidso_sku_id)}
                      data-testid={`sent-back-scrap-${sb.bidso_sku_id}`}
                    >
                      Scrap &amp; delete
                    </Button>
                  </div>
                </div>
              );
            })}
            {(liveStats.sent_back_items || []).length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">No sent-back SKUs right now.</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===================== WIZARD (Paths 1 + 2 BOM attach) ===================== */}
      {view === "wizard" && (
        <>
          {entryReason === "sent-back" && (
            <div className="rounded-md border-2 border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid="wizard-remarks-banner">
              <MessageSquareWarning className="w-4 h-4 text-red-600 mt-0.5"/>
              <div className="text-sm">
                <span className="font-semibold text-red-900">Tech Ops remarks:</span>{" "}
                {/* Jul 2026 — Fix & resubmit now routes through a picker,
                    so this must be the currently-loaded SKU's remarks,
                    not always `sent_back_items[0]`. */}
                <span className="text-red-800">{sentBackInfo?.review_remarks || "(none)"}</span>
              </div>
            </div>
          )}

      {/* Stepper */}
      <div className="flex items-center gap-2" data-testid="step-progress">
        {[1, 2, 3].map(n => (
          <div key={n} className="flex items-center gap-2 flex-1">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${step === n ? "bg-violet-600 text-white border-violet-600" : step > n ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-zinc-500 border-zinc-300"}`}>
              {step > n ? <Check className="w-4 h-4"/> : n}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Step {n}</div>
              <div className="text-sm font-medium">{stepLabel(n)}</div>
            </div>
            {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-emerald-500" : "bg-zinc-200"}`}/>}
          </div>
        ))}
      </div>

      {/* ---------- STEP 1 — Pick existing SKU + source (Tile 1: Attach BOM) ---------- */}
      {step === 1 && (
        <Card data-testid="step-1">
          <CardHeader>
            <CardTitle className="text-lg">Step 1 · Pick the Bidso SKU &amp; a source to clone from</CardTitle>
            <CardDescription>
              These Bidso SKUs already have codes but no BOM yet. Pick one, then pick an ACTIVE source SKU
              whose recipe you want to clone — colour rows blank, structural parts copy as-is.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-600">Bidso SKU (no BOM)</label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid="target-select"><SelectValue placeholder={liveEmptyBomSkus.length ? "Pick a Bidso SKU…" : "No DRAFT_NO_BOM SKUs — click 'Generate new code' on the landing"}/></SelectTrigger>
                <SelectContent>
                  {liveEmptyBomSkus.map(s => (
                    <SelectItem key={s.bidso_sku_id} value={s.bidso_sku_id} data-testid={`target-option-${s.bidso_sku_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{s.bidso_sku_id}</span>
                        {s.name ? <span className="text-zinc-500"> — {s.name}</span> : null}
                        {s._scan_origin === "legacy" && (
                          <Badge
                            variant="outline"
                            className="ml-1 text-[10px] border-amber-400 bg-amber-50 text-amber-800"
                            data-testid={`target-legacy-badge-${s.bidso_sku_id}`}
                          >
                            {s.status || "LEGACY"} · no BOM
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {targetId && (() => {
                const live = liveEmptyBomSkus.find(s => s.bidso_sku_id === targetId);
                if (!live) return null;
                const isLegacy = live._scan_origin === "legacy";
                return (
                  <div
                    className={`rounded-md border-2 p-3 ${isLegacy ? "border-amber-300 bg-amber-50" : "border-violet-200 bg-violet-50"}`}
                    data-testid="target-preview"
                  >
                    <div className={`text-[10px] uppercase tracking-wider font-mono ${isLegacy ? "text-amber-800" : "text-violet-700"}`}>
                      Attaching BOM to
                    </div>
                    <div className={`text-xl font-mono font-bold mt-1 ${isLegacy ? "text-amber-900" : "text-violet-900"}`}>
                      {live.bidso_sku_id}
                    </div>
                    <div className={`text-xs mt-1 ${isLegacy ? "text-amber-800" : "text-violet-700"}`}>
                      Status <span className="font-mono">{live.status || "DRAFT_NO_BOM"}</span>
                      {isLegacy && (
                        <span className="ml-2">
                          · legacy orphan — will be promoted to <span className="font-mono">DRAFT_NO_BOM</span> on next step
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider font-mono text-zinc-600">Source (ACTIVE)</label>
              <Popover open={sourcePopoverOpen} onOpenChange={setSourcePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                    data-testid="source-select"
                  >
                    {sourceId ? (
                      <span className="font-mono truncate">{sourceId}</span>
                    ) : (
                      <span className="text-zinc-500">Pick an ACTIVE bidso SKU to clone…</span>
                    )}
                    <Search className="w-4 h-4 ml-2 opacity-50"/>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Type to search Bidso SKU id or name…"
                      value={sourceSearch}
                      onValueChange={setSourceSearch}
                      data-testid="source-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>No matching ACTIVE Bidso SKU.</CommandEmpty>
                      <CommandGroup>
                        {liveActiveSkus.map((s) => (
                          <CommandItem
                            key={s.bidso_sku_id}
                            value={s.bidso_sku_id}
                            onSelect={() => {
                              setSourceId(s.bidso_sku_id);
                              setSourcePopoverOpen(false);
                            }}
                            data-testid={`source-option-${s.bidso_sku_id}`}
                          >
                            <span className="font-mono">{s.bidso_sku_id}</span>
                            {s.name ? <span className="ml-2 text-zinc-500 text-xs truncate">— {s.name}</span> : null}
                            {s.license_code ? <Badge variant="outline" className="ml-2 text-[10px]">{s.license_code}</Badge> : null}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {sourceId && (
                <div className="rounded-md border border-violet-200 bg-violet-50 p-3 text-sm">
                  <div className="font-semibold text-violet-900 font-mono">{sourceId}</div>
                  <div className="text-violet-800 text-xs mt-1">Its BOM will clone over · colourable rows blanked.</div>
                </div>
              )}
            </div>
          </CardContent>
          <CardContent className="flex justify-end gap-2 border-t pt-4">
            <Button onClick={advanceAttach} data-testid="next-to-compose" disabled={!targetId || !sourceId}>
              Import BOM &amp; compose colours <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ---------- SENT-BACK banner (when designer is editing a Tech-Ops-rejected SKU) ---------- */}
      {sentBackInfo && (step === 1 || step === 2 || step === 3) && (
        <Card className="border-red-300 bg-red-50/40" data-testid="sent-back-banner">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <CardTitle className="text-base text-red-900">
                  Sent back by Tech Ops · <span className="font-mono">{sentBackInfo.bidso_sku_id}</span>
                </CardTitle>
                <CardDescription className="text-red-800 mt-1 text-xs font-mono">
                  {sentBackInfo.reviewed_by_name && (<>by {sentBackInfo.reviewed_by_name}</>)}
                  {sentBackInfo.reviewed_at && (<> · {new Date(sentBackInfo.reviewed_at).toLocaleString()}</>)}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-red-900 whitespace-pre-wrap" data-testid="sent-back-remarks">
              {sentBackInfo.review_remarks || "(No remarks provided)"}
            </div>
            {sentBackInfo.tech_ops_edit_summary && (
              <div className="mt-3 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 flex items-start gap-2" data-testid="tech-ops-diff">
                <Pencil className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold">Tech Ops modified your BOM:</div>
                  <div className="font-mono mt-0.5">{sentBackInfo.tech_ops_edit_summary}</div>
                  {sentBackInfo.tech_ops_modified_by_name && sentBackInfo.tech_ops_modified_at && (
                    <div className="text-[10px] text-violet-700 mt-1">
                      by {sentBackInfo.tech_ops_modified_by_name} · {new Date(sentBackInfo.tech_ops_modified_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="text-xs text-red-700 mt-3">
              The current BOM (with Tech Ops&apos;s edits if any) is loaded below. Adjust as needed and resubmit.
            </div>
            {/* Jul 2026 — Designer escape hatch. If the send-back is
                fundamentally wrong (wrong SKU code, mistaken clone, etc.)
                the composer can wipe the composition + DRAFT parts and
                return the SKU code to the empty-BOM pool for a fresh
                compose, without being forced to fix-and-resubmit. */}
            <div className="mt-4 pt-3 border-t border-red-200 flex items-center justify-between gap-3">
              <div className="text-xs text-red-800">
                <span className="font-semibold">Or scrap this composition</span> — deletes the
                BOM + DRAFT parts this SKU minted (reused parts are kept). SKU code is retained
                and returns to the &ldquo;needs BOM&rdquo; pool.
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => scrapSentBackSku(sentBackInfo.bidso_sku_id)}
                data-testid="scrap-sent-back-btn"
              >
                Scrap &amp; delete
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- STEP 2 — Compose ---------- */}
      {step === 2 && (
        <Card data-testid="step-2">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-lg">Step 2 · Pick pantone shades for editable parts</CardTitle>
              <CardDescription className="mt-1">
                INP and INM parts need a pantone pick. ACC/hardware carry over from source as-is.
                Pantone list is the existing library only — to use a new shade, add it to Tech Ops → Pantone first.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => openMouldPicker(null)} data-testid="add-part-btn">
                <Plus className="w-4 h-4 mr-2"/>Add part
              </Button>
              <Button variant="outline" onClick={openAccPicker} data-testid="add-accessory-btn">
                <Plus className="w-4 h-4 mr-2"/>Add accessory
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Path 1 only — let designer flip between blank-pantone (default,
                forces fresh picks) and preserve-from-source (carries source
                colours over for quick tweaks). Path 3 always preserves. */}
            {entryReason === "attach" && (
              <div className="flex items-center justify-between rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2" data-testid="attach-colour-mode">
                <div className="text-xs text-violet-900">
                  <span className="font-semibold">Source colours:</span>{" "}
                  {attachColourMode === "blank"
                    ? "every INP/INM row starts blank — pick each pantone consciously."
                    : "every INP/INM row carries the source SKU's pantone — tweak what differs."}
                </div>
                <div className="inline-flex rounded-md overflow-hidden border border-violet-300">
                  <button
                    type="button"
                    onClick={() => applyAttachColourMode("blank")}
                    className={`px-3 py-1 text-xs font-mono uppercase ${attachColourMode === "blank" ? "bg-violet-600 text-white" : "bg-white text-violet-700 hover:bg-violet-100"}`}
                    data-testid="mode-blank-btn"
                  >
                    Start blank
                  </button>
                  <button
                    type="button"
                    onClick={() => applyAttachColourMode("preserve")}
                    className={`px-3 py-1 text-xs font-mono uppercase border-l border-violet-300 ${attachColourMode === "preserve" ? "bg-violet-600 text-white" : "bg-white text-violet-700 hover:bg-violet-100"}`}
                    data-testid="mode-preserve-btn"
                  >
                    Start from source
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600 font-mono">
                  <tr>
                    <th className="text-left p-3">Cat</th>
                    <th className="text-left p-3">Mould</th>
                    <th className="text-left p-3">Part name</th>
                    <th className="text-left p-3">Pantone</th>
                    <th className="text-right p-3">Qty</th>
                    <th className="text-right p-3 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    // Live dedup preview from the backend (single source of
                    // truth = find_duplicate_rm). Falls back to the cloned
                    // source rm_id when the designer hasn't changed pantone.
                    const preview = dedupPreviews[idx];
                    const sourceUnchanged = r._editable && isSourceUnchanged(r);
                    const existingRmRef = sourceUnchanged
                      ? r._source_rm_id
                      : (preview?.action === "existing" ? preview.rm_id : null);
                    // While the debounced preview is in flight, never
                    // claim "new INP" — the answer simply isn't known yet.
                    // The shortcut for source-unchanged rows resolves
                    // instantly, so we only show "checking…" when the
                    // shortcut doesn't apply AND no preview has arrived
                    // for this index yet.
                    const isChecking =
                      r._editable && r.pantone && !sourceUnchanged &&
                      dedupChecking && preview === undefined;
                    const isNew = r._editable && r.pantone && !existingRmRef && !isChecking;
                    // Category-aware "new" badge text. ACC must never say
                    // "new INP" — that mislabel is the bug the user flagged.
                    const newBadgeLabel = `new ${(r.category || "INP").toUpperCase()}`;
                    return (
                      <tr key={idx} className={`border-b ${!r._editable ? "bg-zinc-50/60" : ""}`}>
                        <td className="p-3">
                          <Badge variant={r._editable ? "default" : "secondary"} className="font-mono text-[10px]">
                            {r.category}
                            {!r._editable && <Lock className="w-3 h-3 ml-1"/>}
                          </Badge>
                          {r._origin === "added" && <Badge variant="outline" className="ml-2 text-[10px]">added</Badge>}
                          {r._origin === "replaced" && <Badge variant="outline" className="ml-2 text-[10px]">replaced</Badge>}
                        </td>
                        <td className="p-3 font-mono text-xs">{r.mould_code}</td>
                        <td className="p-3" data-testid={`row-part-label-${idx}`}>
                          {/* R-Composer-desc (Jul 2026) — human-readable
                              label (part_name for INP/INM, description
                              for ACC, rm_id last-resort) + always show
                              the rm_id below as a mono chip so
                              traceability is preserved. */}
                          <div className="text-sm text-zinc-800">
                            {r.part_name || r.description || r.rm_id}
                          </div>
                          <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                            {r.rm_id}
                          </div>
                        </td>
                        <td className="p-3">
                          {r._editable ? (
                            <div className="flex items-center gap-2">
                              <Select value={r.pantone} onValueChange={(v) => updatePantone(idx, v)}>
                                <SelectTrigger className="w-56" data-testid={`pantone-${idx}`}>
                                  <SelectValue placeholder="Pick pantone…"/>
                                </SelectTrigger>
                                <SelectContent>
                                  {/* Case-insensitive existence check —
                                      `BLACK 6 C` == `Black 6 C` but whitespace
                                      is preserved (`Black 6 C` ≠ `Black 6C`). */}
                                  {r.pantone && !livePantones.some(p => (p.pantone_code || "").toLowerCase() === (r.pantone || "").toLowerCase()) && (
                                    <SelectItem key={`__inline_${r.pantone}`} value={r.pantone}>
                                      <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm border border-amber-400" style={{background: "#fde68a"}}/>
                                        <span className="font-mono text-xs">{r.pantone}</span>
                                        <span className="text-amber-700 text-xs italic">(not in master)</span>
                                      </div>
                                    </SelectItem>
                                  )}
                                  {livePantones.map(p => (
                                    <SelectItem key={p.id || p.pantone_code} value={p.pantone_code}>
                                      <div className="flex items-center gap-2">
                                        <span className="inline-block w-3 h-3 rounded-sm" style={{background: p.color_hex || "#ccc"}}/>
                                        <span className="font-mono text-xs">{p.pantone_code}</span>
                                        <span className="text-zinc-500 text-xs">{p.pantone_name}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {existingRmRef && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">→ {existingRmRef}</Badge>}
                              {isChecking && <Badge className="bg-slate-100 text-slate-600 border-slate-200 animate-pulse" data-testid={`dedup-checking-${idx}`}>checking…</Badge>}
                              {isNew && <Badge className="bg-amber-100 text-amber-800 border-amber-200">{newBadgeLabel}</Badge>}
                            </div>
                          ) : (
                            <div className="text-zinc-500 italic">carried over</div>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono">{r.qty}</td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openMouldPicker(idx)} title="Replace mould">
                            <Replace className="w-4 h-4"/>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} title="Remove">
                            ×
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No parts. Pick a different source in Step 1 or add a mould.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2"/>Back
              </Button>
              <div className="flex items-center gap-3 text-sm text-zinc-600">
                <span><Lock className="w-3 h-3 inline mr-1"/>{rows.filter(r => !r._editable).length} non-editable</span>
                <CircleDot className="w-3 h-3"/>
                <span>{rows.filter(r => r._editable).length} editable · {rows.filter(r => r._editable && r.pantone).length} filled</span>
              </div>
              <Button onClick={() => setStep(3)} data-testid="next-to-review">
                Review <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---------- STEP 3 — Review ---------- */}
      {step === 3 && (
        <Card data-testid="step-3">
          <CardHeader>
            <CardTitle className="text-lg">Step 3 · Review & submit for Tech Ops approval</CardTitle>
            <CardDescription>
              Verify the new vs. existing INP split. On submit the bidso SKU moves to <span className="font-mono">PENDING_REVIEW</span> and shows up in Tech Ops → Bidso SKU Requests.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {dupCheck.match && (
              <div className="rounded-md border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="duplicate-banner">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <div className="font-semibold text-red-900">Duplicate BOM detected</div>
                  <div className="text-sm text-red-800 mt-1">
                    An existing bidso SKU already has the exact same (parts × pantones) signature:
                    <Badge className="ml-2 font-mono" data-testid="duplicate-banner-sku-id">
                      {dupCheck.match.matched_bidso_sku_id}
                    </Badge>
                    {dupCheck.match.status && (
                      <Badge variant="outline" className="ml-1 font-mono text-[10px]" data-testid="duplicate-banner-status">
                        {dupCheck.match.status}
                      </Badge>
                    )}
                  </div>
                  {(dupCheck.match.description || dupCheck.match.vertical_code || dupCheck.match.model_name) && (
                    <div className="text-xs text-red-700 mt-1" data-testid="duplicate-banner-detail">
                      {dupCheck.match.description || [dupCheck.match.vertical_code, dupCheck.match.model_name].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div className="text-xs text-red-700 mt-2">
                    Submit is blocked. Change at least one pantone or part to differentiate — or open the matching SKU to compare.
                  </div>
                </div>
              </div>
            )}
            {dupCheck.loading && (
              <div className="text-xs text-stone-500 italic" data-testid="duplicate-check-loading">
                Checking for an existing matching BOM…
              </div>
            )}

            {review.missingPantone.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="missing-pantone-banner">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold text-amber-900">{review.missingPantone.length} editable part(s) missing a pantone</div>
                  <div className="text-sm text-amber-800 mt-1">
                    Go back and pick a shade for every INP/INM row before submitting.
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CircleDot className="w-4 h-4 text-amber-600"/>
                    New INP/INM to create ({review.newInps.length})
                  </CardTitle>
                  <CardDescription className="text-xs">
                    rm_bom will be auto-rolled-up from each mould&apos;s polymer + MB dosage.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {review.newInps.length === 0 ? (
                    <p className="text-xs text-zinc-500">None — every editable part matched an existing INP/INM.</p>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {review.newInps.map((r, i) => (
                        <li key={i} className="flex items-center justify-between border-b pb-1">
                          <span>
                            <span className="font-mono text-xs">{r.mould_code}</span>
                            {" · "}
                            {/* R-Composer-desc (Jul 2026) — fall back to
                                description then rm_id when part_name is
                                empty (ACC has no part_name). */}
                            {r.part_name || r.description || r.rm_id}
                          </span>
                          <Badge variant="outline" className="font-mono text-[10px]">{r.pantone}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600"/>
                    Existing INP/INM reused ({review.existingInps.length})
                  </CardTitle>
                  <CardDescription className="text-xs">
                    These (mould × pantone) combos already exist — BOM will link to them.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {review.existingInps.length === 0 ? (
                    <p className="text-xs text-zinc-500">None — all editable parts are first-time combos.</p>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {review.existingInps.map((r, i) => (
                        <li key={i} className="flex items-center justify-between border-b pb-1">
                          <span>
                            <span className="font-mono text-xs">{r.mould_code}</span>
                            {" · "}
                            {r.part_name || r.description || r.rm_id}
                          </span>
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 font-mono text-[10px]">{r._matched_rm}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-between border-t pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2"/>Edit pantones
              </Button>
              <div className="flex-1 mx-4 max-w-2xl">
                <label htmlFor="submitter-remarks" className="block text-xs font-semibold text-zinc-700 mb-1">
                  Note for Tech Ops <span className="text-zinc-400 font-normal">(optional)</span>
                </label>
                <Textarea
                  id="submitter-remarks"
                  data-testid="submitter-remarks-input"
                  value={submitterRemarks}
                  onChange={(e) => setSubmitterRemarks(e.target.value.slice(0, 2000))}
                  placeholder="e.g. 'New pantone PMS-185C is a close match — please verify under daylight before approving.'"
                  rows={3}
                  className="text-sm resize-y"
                  maxLength={2000}
                />
                <div className="text-[10px] text-zinc-400 text-right mt-1 font-mono">
                  {submitterRemarks.length} / 2000
                </div>
              </div>
              <Button
                disabled={submitting || dedupChecking || !!dupCheck.match || review.missingPantone.length > 0 || rowsWithUnknownPantone.length > 0}
                onClick={handleSubmitForReview}
                data-testid="submit-btn"
                className="bg-violet-600 hover:bg-violet-700"
                title={
                  dedupChecking
                    ? "Dedup preview still resolving — wait a moment."
                    : rowsWithUnknownPantone.length > 0
                    ? `${rowsWithUnknownPantone.length} row(s) have a pantone not in the master — fix or create the Pantone first.`
                    : ""
                }
              >
                <Send className="w-4 h-4 mr-2"/>{submitting ? "Submitting…" : dedupChecking ? "Checking dedup…" : "Submit for Tech Ops review"}
              </Button>
            </div>

            {/* Inline sticky pre-submit block: pantones not in master */}
            {rowsWithUnknownPantone.length > 0 && (
              <div
                className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
                data-testid="pantone-not-in-master-block"
              >
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"/>
                <div className="text-sm">
                  <div className="font-semibold text-amber-900">
                    Submit blocked: {rowsWithUnknownPantone.length} row(s) use a pantone not in the master library.
                  </div>
                  <div className="text-amber-800 mt-1">
                    Either pick a pantone that exists in the master (dropdown), or create the
                    pantone first in <span className="font-mono">Tech Ops → Pantones</span>.
                  </div>
                  <ul className="list-disc ml-5 mt-2 space-y-0.5 text-xs font-mono text-amber-900">
                    {rowsWithUnknownPantone.slice(0, 5).map((r) => (
                      <li key={r.idx}>Row {r.idx} ({r.mould_code}) → &quot;{r.pantone}&quot;</li>
                    ))}
                    {rowsWithUnknownPantone.length > 5 && (
                      <li>… and {rowsWithUnknownPantone.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Inline sticky post-submit error banner (DUPLICATE_BOM / NO_MATCHING_COLOURANT / other) */}
            {submitError && (
              <div
                className="mt-4 rounded-md border border-red-300 bg-red-50 p-4 flex items-start gap-3"
                data-testid="submit-error-banner"
              >
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"/>
                <div className="text-sm flex-1">
                  <div className="font-semibold text-red-900">
                    {submitError.kind === "DUPLICATE_BOM" && "Duplicate BOM detected"}
                    {submitError.kind === "NO_MATCHING_COLOURANT" && "No matching colourant"}
                    {submitError.kind === "ERROR" && "Submit failed"}
                  </div>
                  <div className="text-red-800 mt-1 whitespace-pre-wrap">{submitError.message}</div>
                  {submitError.kind === "NO_MATCHING_COLOURANT" && (
                    <div className="text-xs text-red-700 mt-2 font-mono">
                      Offending row: {submitError.mould_code} · pantone {submitError.pantone_code}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSubmitError(null)} className="text-red-700 hover:bg-red-100">
                  Dismiss
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------- POST-SUBMIT CONFIRMATION ---------- */}
      {view === "submitted" && submittedInfo && (
        <div className="space-y-6" data-testid="submitted-view">
          {/* Success card */}
          <Card className="border-emerald-300 bg-emerald-50/50">
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-7 h-7 text-emerald-600"/>
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl text-emerald-900">Submitted for Tech Ops review</CardTitle>
                  <CardDescription className="text-emerald-800 mt-1">
                    <span className="font-mono font-semibold">{submittedInfo.bidso_sku_id}</span>
                    {submittedInfo.name ? <> — {submittedInfo.name}</> : null}
                    {" "}is now in <Badge className="bg-amber-100 text-amber-800 border-amber-200 ml-1 font-mono text-[10px]">PENDING_REVIEW</Badge>.
                    The Tech Ops engineer will see it under <span className="font-semibold">Tech Ops → Bidso Reviews</span>.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="rounded-md bg-white border border-emerald-200 p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">BOM items</div>
                  <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="submitted-items-count">{submittedInfo.items_count}</div>
                </div>
                <div className="rounded-md bg-white border border-emerald-200 p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">New INPs created</div>
                  <div className="text-2xl font-bold text-amber-600 mt-1" data-testid="submitted-new-inps">{submittedInfo.new_inps.length}</div>
                </div>
                <div className="rounded-md bg-white border border-emerald-200 p-3">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Existing INPs reused</div>
                  <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="submitted-reused-inps">{submittedInfo.reused_inps.length}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-5 pt-4 border-t border-emerald-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmittedInfo(null);
                    setSentBackInfo(null);
                    setRows([]);
                    setTargetId("");
                    setSourceId("");
                    setStep(1);
                    setView("landing");
                  }}
                  data-testid="submitted-back-to-landing"
                >
                  <ArrowLeft className="w-4 h-4 mr-2"/>Back to composer landing
                </Button>
                <Button
                  variant="default"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => { window.location.href = "/techops?tab=bidso-reviews"; }}
                  data-testid="submitted-open-techops"
                >
                  Open Tech Ops review queue →
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My pending reviews list */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Your submissions awaiting Tech Ops review
                <Badge variant="outline" data-testid="my-pending-count">{myPendingReviews.length}</Badge>
              </CardTitle>
              <CardDescription>
                Submissions still in <span className="font-mono">PENDING_REVIEW</span>. The Tech Ops engineer must approve them before they become ACTIVE in the catalogue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myPendingReviews.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center" data-testid="my-pending-empty">
                  No pending submissions found.
                </div>
              ) : (
                <ul className="divide-y" data-testid="my-pending-list">
                  {myPendingReviews.map((r) => {
                    const isCurrent = r.bidso_sku_id === submittedInfo.bidso_sku_id;
                    return (
                      <li
                        key={r.bidso_sku_id}
                        className={`flex items-center justify-between py-3 px-2 ${isCurrent ? "bg-violet-50 border-l-4 border-violet-500 -ml-2 pl-3" : ""}`}
                        data-testid={`my-pending-row-${r.bidso_sku_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-mono">{r.bidso_sku_id}</Badge>
                          <span className="text-sm">{r.name || "—"}</span>
                          {isCurrent && <Badge className="bg-violet-600 text-white text-[10px]">Just submitted</Badge>}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mould picker dialog */}
      <Dialog open={mouldPickerOpen} onOpenChange={setMouldPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{replaceRowIdx === null ? "Add part from Mould Master" : "Replace mould"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
              <Input
                value={mouldSearch}
                onChange={(e) => setMouldSearch(e.target.value)}
                placeholder="Search mould_code or part_name…"
                className="pl-9"
              />
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600 font-mono sticky top-0">
                  <tr>
                    <th className="text-left p-2">Mould</th>
                    <th className="text-left p-2">Part name</th>
                    <th className="text-left p-2">Model</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {liveMoulds.filter(m =>
                    !mouldSearch ||
                    (m.mould_code || "").toLowerCase().includes(mouldSearch.toLowerCase()) ||
                    (m.part_name || "").toLowerCase().includes(mouldSearch.toLowerCase()) ||
                    (m.model_name || "").toLowerCase().includes(mouldSearch.toLowerCase())
                  ).slice(0, 500).map(m => (
                    <tr key={m.id || m.mould_code} className="border-b hover:bg-zinc-50">
                      <td className="p-2 font-mono">{m.mould_code}</td>
                      <td className="p-2">{m.part_name}</td>
                      <td className="p-2 text-xs text-zinc-500">{m.model_name || m.model_code || ""}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => pickMould(m)}>Pick</Button>
                      </td>
                    </tr>
                  ))}
                  {liveMoulds.length === 0 && (
                    <tr><td colSpan="4" className="p-6 text-center text-zinc-500 text-sm">Loading moulds…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-500">
              Showing ACTIVE moulds only. Pantone will be assigned in the row after pick.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Accessory (ACC) picker dialog — starts empty, searches as you type */}
      <Dialog open={accPickerOpen} onOpenChange={setAccPickerOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add accessory (ACC)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
              <Input
                value={accSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  setAccSearch(v);
                  setAccDupNoticeFor("");
                  if (v.trim().length < 2) setAccResults([]);
                }}
                placeholder="Type ≥2 chars: rm_id, type, model, specs, pantone…"
                className="pl-9"
                autoFocus
                data-testid="add-accessory-search"
              />
            </div>
            {accDupNoticeFor && (
              <div
                className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2"
                data-testid="add-accessory-dup-notice"
              >
                <strong>{accDupNoticeFor}</strong> is already in the BOM — pick a different ACC.
              </div>
            )}
            <div className="max-h-96 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600 font-mono sticky top-0">
                  <tr>
                    <th className="text-left p-2">RM ID</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Model</th>
                    <th className="text-left p-2">Specs</th>
                    <th className="text-left p-2">Pantone</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {accSearching && (
                    <tr><td colSpan="6" className="p-6 text-center text-zinc-500 text-sm">Searching…</td></tr>
                  )}
                  {!accSearching && accSearch.trim().length < 2 && (
                    <tr><td colSpan="6" className="p-6 text-center text-zinc-500 text-sm">
                      Start typing to search the ACC catalog.
                    </td></tr>
                  )}
                  {!accSearching && accSearch.trim().length >= 2 && accResults.length === 0 && (
                    <tr><td colSpan="6" className="p-6 text-center text-zinc-500 text-sm">No ACC matches.</td></tr>
                  )}
                  {!accSearching && accResults.map((acc) => {
                    const cd = acc.category_data || {};
                    const pantone = cd.pantone_code || cd.pantone || cd.colour || "";
                    const already = rows.some((r) => r.rm_id === acc.rm_id);
                    return (
                      <tr key={acc.rm_id} className="border-b hover:bg-zinc-50">
                        <td className="p-2 font-mono text-xs">{acc.rm_id}</td>
                        <td className="p-2 text-xs">{cd.type || "—"}</td>
                        <td className="p-2 text-xs">{cd.model_name || "—"}</td>
                        <td className="p-2 text-xs text-zinc-600">{cd.specs || acc.description || "—"}</td>
                        <td className="p-2 text-xs font-mono">{pantone || "—"}</td>
                        <td className="p-2 text-right">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => pickAcc(acc)}
                            disabled={already}
                            data-testid={`add-accessory-pick-${acc.rm_id}`}
                          >
                            {already ? "In BOM" : "Pick"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-500">
              ACC catalog only (INACTIVE / MERGED hidden). The row is added with the source pantone — change it in the Step 2 table to recolour via the ACC resolver.
            </p>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* ===================== END ===================== */}
    </div>
  );
}
